const LOGIN_AZURE_CALLBACK_PATH = '/login/azure/callback';
const PKCE_VERIFIER_KEY = 'azure_pkce_verifier';
const AZURE_SCOPES = 'openid profile email';

const isConfigured = (value?: string) =>
  Boolean(value && !value.startsWith('%REACT_APP_'));

export const isAzureLoginConfigured = () =>
  isConfigured(config.AZURE_CLIENT_ID) && isConfigured(config.AZURE_TENANT_ID);

const getRedirectUri = () => window.location.origin;

const getAuthorizeUrl = () =>
  `https://login.microsoftonline.com/${config.AZURE_TENANT_ID}/oauth2/v2.0/authorize`;

const getTokenUrl = () =>
  `https://login.microsoftonline.com/${config.AZURE_TENANT_ID}/oauth2/v2.0/token`;

const toBase64Url = (bytes: Uint8Array) => {
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join(
    '',
  );
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const createCodeVerifier = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
};

const createCodeChallenge = async (verifier: string) => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return toBase64Url(new Uint8Array(digest));
};

let redirectResultPromise: Promise<string | null> | undefined;

export const hasAzureOAuthReturn = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
};

export const maybeRedirectAzureCallback = () => {
  if (!hasAzureOAuthReturn()) {
    return;
  }
  if (window.location.pathname.includes(LOGIN_AZURE_CALLBACK_PATH)) {
    return;
  }
  window.location.replace(
    `${window.location.origin}${LOGIN_AZURE_CALLBACK_PATH}${window.location.search}`,
  );
};

export const startAzureLogin = async () => {
  if (!isAzureLoginConfigured()) {
    throw new Error('Connexion Azure non configurée');
  }

  const verifier = createCodeVerifier();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  const challenge = await createCodeChallenge(verifier);
  const url = new URL(getAuthorizeUrl());
  url.searchParams.set('client_id', config.AZURE_CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', getRedirectUri());
  url.searchParams.set('response_mode', 'query');
  url.searchParams.set('scope', AZURE_SCOPES);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
};

const exchangeCodeForIdToken = async (code: string) => {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  if (!verifier) {
    throw new Error('Erreur de connexion');
  }

  const body = new URLSearchParams({
    client_id: config.AZURE_CLIENT_ID,
    grant_type: 'authorization_code',
    code,
    redirect_uri: getRedirectUri(),
    code_verifier: verifier,
    scope: AZURE_SCOPES,
  });
  const response = await fetch(getTokenUrl(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });
  const payload = await response.json();
  if (!response.ok || !payload.id_token) {
    throw new Error(payload.error_description || 'Erreur de connexion');
  }
  return payload.id_token as string;
};

export const getAzureIdTokenFromRedirect = async () => {
  if (!redirectResultPromise) {
    redirectResultPromise = readAzureRedirect();
  }
  return redirectResultPromise;
};

const readAzureRedirect = async () => {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error_description') || params.get('error');
  const code = params.get('code');

  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${window.location.hash}`,
  );

  if (error) {
    throw new Error(error);
  }
  if (!code) {
    return null;
  }
  return exchangeCodeForIdToken(code);
};
